import { AbstractTranscriptEditorCommand } from '../commands/AbstractTranscriptEditorCommand';
import { Segment } from '../models/Segment';

/**
 * Editor reference type
 */
interface EditorRef {
  saveChanges(): Promise<boolean>;
  notifyObservers(event: string, data: any): void;
}

/**
 * Manages the execution, undo, and redo of transcript commands
 * 
 * This command manager implements the Command pattern with undo/redo stacks.
 * It also includes auto-save functionality for immediate persistence of changes
 * after command execution.
 */
export class TranscriptEditorCommandManager {
  /** Stack of commands that can be undone */
  private undoStack: AbstractTranscriptEditorCommand[] = [];
  
  /** Stack of commands that can be redone */
  private redoStack: AbstractTranscriptEditorCommand[] = [];
  
  /** Maximum size of the undo/redo stacks */
  private maxStackSize: number = 50;
  
  /** Flag to control auto-save behavior */
  private autoSaveEnabled: boolean = true;
  
  /** Delay in ms before auto-saving (to debounce rapid changes) */
  private autoSaveDelay: number = 500;
  
  /** Timer handle for debounced auto-save */
  private autoSaveTimer: number | null = null;
  
  /** Reference to the editor instance for auto-save functionality */
  private editorRef: EditorRef | null = null;

  /**
   * Sets the editor reference for auto-save functionality
   * 
   * @param editor The editor instance to use for auto-save
   */
  setEditor(editor: EditorRef): void {
    this.editorRef = editor;
  }

  /**
   * Execute a command and add it to the undo stack
   * Includes auto-save functionality to persist changes immediately
   * Also handles post-command hooks like segment/paragraph sorting
   * 
   * @param command The command to execute
   * @returns Result of the command execution
   */
  async execute(command: AbstractTranscriptEditorCommand): Promise<boolean> {
    // Execute the command
    const result = await Promise.resolve(command.execute());
    
    // If command succeeded, add it to undo stack and handle post-execution hooks
    if (result) {
      this.undoStack.push(command);
      // Clear redo stack since we've taken a new action
      this.redoStack = [];
      
      // Limit stack size
      if (this.undoStack.length > this.maxStackSize) {
        this.undoStack.shift();
      }
      
      // Log the command execution
      console.log(`Executed command: ${command.getDescription()}`);
      
      // Handle post-execution hooks: check if this command requires sorting
      await this.handlePostExecutionHooks(command);
      
      // Notify observers that a command was executed (for undo/redo UI updates)
      if (this.editorRef && typeof this.editorRef.notifyObservers === 'function') {
        this.editorRef.notifyObservers('operation_applied', {
          command: command.getDescription(),
          undoStackSize: this.undoStack.length,
          redoStackSize: this.redoStack.length
        });
      }
      
      // Automatically save changes after each command if auto-save is enabled
      this.autoSaveChanges(command);
    }
    
    return result;
  }

  // Timeout for debouncing optimization operations
  private optimizeTimeout: number | null = null;
  
  /**
   * Handle post-execution hooks for commands
   * This includes:
   * 1. Sorting segments and paragraphs if required
   * 2. Validating and optimizing the container (removing empty elements, merging segments)
   * 
   * @param command The command that was executed
   */
  private async handlePostExecutionHooks(command: AbstractTranscriptEditorCommand): Promise<void> {
    try {
      // We need to cast to access specific methods that aren't in the EditorRef interface
      if (!this.editorRef) return;
      
      // Check if this command needs heavy optimization - speaker commands can skip this
      if (command.getNeedsHeavyOptimization && command.getNeedsHeavyOptimization() === false) {
        // For speaker commands, run optimization offthread to keep UI responsive
        this.scheduleIdleOptimization(command);
        return;
      }
      
      const editor = this.editorRef as unknown as {
        sortSegmentsByTime(): void;
        getContainer(): { segments: Array<{ sortParagraphsByTime(): void }> };
        validateAndOptimizeContainer(performMerge: boolean): { 
          valid: boolean; 
          stats: {
            emptySegmentsRemoved: number;
            emptyParagraphsRemoved: number;
            segmentsMerged: number;
            elementsReassigned: number;
          }
        };
        sortParagraphsByTime(segment: Segment): void;
      };
      
      // Track if we should notify observers after hooks complete
      let shouldNotifyObservers = false;
      
      // Check if command requires sorting
      if (command.requiresSorting && command.requiresSorting()) {
        console.log(`Command "${command.getDescription()}" requires sorting - applying segment and paragraph sorting`);
        
        // Sort all segments by time
        editor.sortSegmentsByTime();
        
        // Sort paragraphs within each segment
        const container = editor.getContainer();
        if (container && container.segments) {
          for (const segment of container.segments) {
            editor.sortParagraphsByTime(segment as unknown as Segment);
          }
        }
        
        shouldNotifyObservers = true;
      }
      
      // Always validate and optimize the container
      console.log(`Running validation and optimization after command "${command.getDescription()}"`);
      const validationResult = editor.validateAndOptimizeContainer(true);
      
      // Log validation statistics
      if (validationResult.stats.emptySegmentsRemoved > 0 || 
          validationResult.stats.emptyParagraphsRemoved > 0 ||
          validationResult.stats.segmentsMerged > 0) {
        shouldNotifyObservers = true;
      }
      
      // Notify observers about changes if needed
      if (shouldNotifyObservers && this.editorRef && typeof this.editorRef.notifyObservers === 'function') {
        // Import the TranscriptEditorChangeType dynamically to avoid circular dependencies
        const { TranscriptEditorChangeType } = require('../types');
        
        this.editorRef.notifyObservers(TranscriptEditorChangeType.POST_HOOKS_APPLIED, {
          commandDescription: command.getDescription(),
          validationStats: validationResult.stats
        });
      }
    } catch (error) {
      console.error('Error in post-execution hooks:', error);
    }
  }
  
  /**
   * Schedule optimization to run during idle time
   * This improves UI responsiveness for speaker assignments
   * 
   * @param command The command that was executed
   */
  private scheduleIdleOptimization(command: AbstractTranscriptEditorCommand): void {
    // Cancel any pending optimization
    if (this.optimizeTimeout !== null) {
      clearTimeout(this.optimizeTimeout);
    }
    
    // Debounce optimization to once every 1000ms
    this.optimizeTimeout = window.setTimeout(() => {
      // Use requestIdleCallback if available, otherwise setTimeout
      const scheduleForIdle = typeof window.requestIdleCallback === 'function' 
        ? window.requestIdleCallback 
        : (cb: Function) => setTimeout(() => cb({ timeRemaining: () => 10 }), 1);
      
      scheduleForIdle((deadline: { timeRemaining: () => number }) => {
        if (deadline.timeRemaining() > 0 || document.hidden) {
          // Only run if we have idle time or the page is hidden
          this.runOptimizationOffThread(command);
        } else {
          // Reschedule if we don't have enough idle time
          setTimeout(() => this.scheduleIdleOptimization(command), 100);
        }
      }, { timeout: 2000 }); // Ensure it runs within 2 seconds even if no idle time
    }, 1000);
  }
  
  /**
   * Run optimization operations off the main thread
   * 
   * @param command The command that triggered optimization
   */
  private async runOptimizationOffThread(command: AbstractTranscriptEditorCommand): Promise<void> {
    try {
      if (!this.editorRef) return;
      
      const editor = this.editorRef as unknown as {
        validateAndOptimizeContainer(performMerge: boolean): { 
          valid: boolean; 
          stats: {
            emptySegmentsRemoved: number;
            emptyParagraphsRemoved: number;
            segmentsMerged: number;
            elementsReassigned: number;
          }
        };
      };
      
      console.log(`Running deferred optimization after command "${command.getDescription()}"`);
      const validationResult = editor.validateAndOptimizeContainer(true);
      
      // Only notify if we actually made changes
      if ((validationResult.stats.emptySegmentsRemoved > 0 || 
           validationResult.stats.emptyParagraphsRemoved > 0 ||
           validationResult.stats.segmentsMerged > 0) && 
          this.editorRef && 
          typeof this.editorRef.notifyObservers === 'function') {
        
        // Import the TranscriptEditorChangeType dynamically to avoid circular dependencies
        const { TranscriptEditorChangeType } = require('../types');
        
        this.editorRef.notifyObservers(TranscriptEditorChangeType.POST_HOOKS_APPLIED, {
          commandDescription: command.getDescription(),
          validationStats: validationResult.stats,
          deferredOptimization: true
        });
      }
    } catch (error) {
      console.error('Error in deferred optimization:', error);
    } finally {
      this.optimizeTimeout = null;
    }
  }

  /**
   * Auto-save changes if enabled
   * 
   * @param command The command that was executed
   */
  private autoSaveChanges(command: AbstractTranscriptEditorCommand): void {
    if (!this.autoSaveEnabled) return;
    
    // Cancel any pending auto-save
    if (this.autoSaveTimer !== null) {
      clearTimeout(this.autoSaveTimer);
    }
    
    // Schedule a new auto-save
    this.autoSaveTimer = window.setTimeout(async () => {
      // Clear the timer reference
      this.autoSaveTimer = null;
      
      // The editor reference is now stored in the CommandManager, not in commands
      if (this.editorRef) {
        try {
          // Call the editor's save method
          const saveResult = await this.editorRef.saveChanges();
          if (saveResult) {
            console.log('Auto-save successful:', command.getDescription());
          }
        } catch (error) {
          console.error('Error during auto-save:', error);
        }
      }
    }, this.autoSaveDelay);
  }

  /**
   * Undo the last command
   * 
   * @returns True if a command was undone, false otherwise
   */
  async undo(): Promise<boolean> {
    // Get the last command from undo stack
    const command = this.undoStack.pop();
    if (!command) {
      return false;
    }
    
    console.log(`Undoing: ${command.getDescription()}`);
    
    // Undo the command
    const result = await Promise.resolve(command.undo());
    
    // If undo succeeded, add command to redo stack
    if (result) {
      this.redoStack.push(command);
      
      // Limit stack size
      if (this.redoStack.length > this.maxStackSize) {
        this.redoStack.shift();
      }
      
      console.log(`Undo successful, command index now: ${this.undoStack.length}`);

      // Save changes after undo operation
      this.autoSaveChanges(command);

      // Notify observers through the editor reference
      if (this.editorRef && typeof this.editorRef.notifyObservers === 'function') {
        this.editorRef.notifyObservers('operation_applied', {
          command: `Undid: ${command.getDescription()}`,
          undoStackSize: this.undoStack.length,
          redoStackSize: this.redoStack.length
        });
      }
    } else {
      // If undo failed, put command back on undo stack
      this.undoStack.push(command);
      console.warn('Failed to undo command');
    }
    
    return result;
  }

  /**
   * Redo the last undone command
   * 
   * @returns True if a command was redone, false otherwise
   */
  async redo(): Promise<boolean> {
    // Get the last command from redo stack
    const command = this.redoStack.pop();
    if (!command) {
      return false;
    }
    
    console.log(`Redoing: ${command.getDescription()}`);
    
    // Execute the command again
    const result = await Promise.resolve(command.execute());
    
    // If redo succeeded, add command back to undo stack
    if (result) {
      this.undoStack.push(command);
      console.log(`Redo successful, command index now: ${this.undoStack.length}`);
      
      // Handle post-execution hooks (like sorting and validation) for redone commands as well
      await this.handlePostExecutionHooks(command);

      // Save changes after redo operation
      this.autoSaveChanges(command);

      // Notify observers through the editor reference
      if (this.editorRef && typeof this.editorRef.notifyObservers === 'function') {
        this.editorRef.notifyObservers('operation_applied', {
          command: `Redid: ${command.getDescription()}`,
          undoStackSize: this.undoStack.length,
          redoStackSize: this.redoStack.length
        });
      }
    } else {
      // If redo failed, put command back on redo stack
      this.redoStack.push(command);
      console.warn('Failed to redo command');
    }
    
    return result;
  }

  /**
   * Get the last executed command
   * 
   * @returns The last command or null if none exists
   */
  getLastCommand(): AbstractTranscriptEditorCommand | null {
    return this.undoStack.length > 0 ? this.undoStack[this.undoStack.length - 1] : null;
  }

  /**
   * Get the number of commands in the undo stack
   * 
   * @returns Undo stack size
   */
  getUndoStackSize(): number {
    return this.undoStack.length;
  }

  /**
   * Get the number of commands in the redo stack
   * 
   * @returns Redo stack size
   */
  getRedoStackSize(): number {
    return this.redoStack.length;
  }

  /**
   * Clear both undo and redo stacks
   * Should be called when loading a new transcript
   */
  clearStacks(): void {
    this.undoStack = [];
    this.redoStack = [];
  }
  
  /**
   * Toggle auto-save functionality
   * 
   * @param enabled Whether auto-save should be enabled
   */
  setAutoSaveEnabled(enabled: boolean): void {
    this.autoSaveEnabled = enabled;
  }
  
  /**
   * Set the auto-save delay
   * 
   * @param delay Delay in milliseconds
   */
  setAutoSaveDelay(delay: number): void {
    this.autoSaveDelay = delay;
  }
  
  /**
   * Clean up resources
   * Should be called when the editor is being destroyed
   */
  cleanup(): void {
    if (this.autoSaveTimer !== null) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
    
    if (this.optimizeTimeout !== null) {
      clearTimeout(this.optimizeTimeout);
      this.optimizeTimeout = null;
    }
    
    this.clearStacks();
    this.editorRef = null;
  }
}